import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Trash2 } from "lucide-react";
import { api, apiError, imgUrl, formatINR } from "../../lib/api";
import { toast } from "sonner";
import { inputCls } from "./shared";

const ORDER_STATUSES = ["placed", "confirmed", "processing", "shipped", "delivered", "cancelled"];
const PAYMENT_STATUSES = ["pending", "pay_on_delivery", "awaiting_verification", "verified", "rejected"];

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/orders/${id}`, body);
      toast.success("Order updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.order_reference} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success("Order deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => orders.filter((o) => {
    if (statusFilter && o.order_status !== statusFilter) return false;
    if (!q.trim()) return true;
    const hay = `${o.order_reference} ${o.customer?.name} ${o.customer?.phone} ${o.confirmation_code || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  }), [orders, q, statusFilter]);

  return (
    <div data-testid="admin-orders-tab">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or order ref…"
            className={`${inputCls} mt-0 pl-10`} />
        </div>
        <select data-testid="orders-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-input bg-white/80 px-3 py-2.5 text-xs uppercase outline-none">
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {filtered.length === 0 && <p className="glass rounded-2xl p-8 text-center text-sm font-light text-muted-foreground" data-testid="orders-empty">No orders match — they&#8217;ll appear here as customers check out.</p>}
      <div className="space-y-4">
        {filtered.map((o) => (
          <div key={o.id} className="glass rounded-2xl p-5" data-testid={`admin-order-${o.order_reference}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg">{o.order_reference} <span className="text-sm text-muted-foreground">· {formatINR(o.total)}</span>
                  {o.confirmation_code && <span className="ml-2 rounded-full bg-accent px-2.5 py-0.5 text-[10px] tracking-widest text-accent-foreground uppercase">{o.confirmation_code}</span>}
                </p>
                <p className="text-xs font-light text-muted-foreground">{o.customer.name} · {o.customer.phone} · {o.customer.address}{o.customer.city ? `, ${o.customer.city}` : ""}{o.customer.pincode ? ` ${o.customer.pincode}` : ""} · shipping ₹{o.shipping ?? 0}</p>
                <p className="mt-0.5 text-[10px] font-light tracking-widest text-muted-foreground uppercase">{new Date(o.created_at).toLocaleString("en-IN")}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid={`order-status-${o.order_reference}`} value={o.order_status} onChange={(e) => patch(o.id, { order_status: e.target.value })}
                  className="rounded-full border border-input bg-white/80 px-3 py-1.5 text-xs uppercase outline-none">
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid={`payment-status-${o.order_reference}`} value={o.payment_status} onChange={(e) => patch(o.id, { payment_status: e.target.value })}
                  className="rounded-full border border-input bg-white/80 px-3 py-1.5 text-xs uppercase outline-none">
                  {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                {o.payment_proof_url && (
                  <a data-testid={`proof-link-${o.order_reference}`} href={imgUrl(o.payment_proof_url)} target="_blank" rel="noreferrer"
                    className="rounded-full bg-accent px-3 py-1.5 text-xs uppercase text-accent-foreground hover:opacity-80">View proof</a>
                )}
                <button data-testid={`order-delete-${o.order_reference}`} onClick={() => remove(o)} aria-label="Delete order"
                  className="rounded-full p-1.5 hover:bg-secondary hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            </div>
            <ul className="mt-3 space-y-1 border-t border-input/60 pt-3 text-xs font-light text-muted-foreground">
              {o.items.map((i) => <li key={i.product_id + (i.variant || "")}>{i.name}{i.variant ? ` — Design ${i.variant}` : ""} × {i.qty} — {formatINR(i.price * i.qty)} <span className="uppercase">({o.payment_method.replace(/_/g, " ")})</span></li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
